// AgentFlow-based chat service for managing chat history
// Uses AgentFlow API for chat history and localStorage for caching

import { agentFlowApi } from './agentFlowApi';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface ChatSession {
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

class AgentFlowChatService {
  private getStorageKey(userId: string, sessionId: string): string {
    return `agentflow_chat_${userId}_${sessionId}`;
  }

  private getSessionKey(userId: string): string {
    return `agentflow_session_${userId}`;
  }

  // Get or create session with persistence
  async getOrCreateSession(userId: string): Promise<string> {
    const sessionKey = this.getSessionKey(userId);
    const existingSessionId = localStorage.getItem(sessionKey);
    
    if (existingSessionId) {
      // Clean up any duplicate sessions for this user
      this.cleanupDuplicateSessions(userId, existingSessionId);
      return existingSessionId;
    }
    
    // Create new session
    const newSessionId = await this.createSession(userId);
    localStorage.setItem(sessionKey, newSessionId);
    return newSessionId;
  }

  // Clean up duplicate sessions for a user
  private cleanupDuplicateSessions(userId: string, activeSessionId: string): void {
    const allKeys = Object.keys(localStorage);
    const userChatKeys = allKeys.filter(key => key.startsWith(`agentflow_chat_${userId}_`));
    
    // Remove all sessions except the active one
    userChatKeys.forEach(key => {
      if (!key.endsWith(activeSessionId)) {
        localStorage.removeItem(key);
      }
    });
  }

  // Create a new chat session
  async createSession(userId: string): Promise<string> {
    if (!userId || userId.trim() === '') {
      throw new Error('Invalid user ID provided');
    }

    // Check if there's an existing session in localStorage
    const existingSessionId = localStorage.getItem(this.getSessionKey(userId));
    if (existingSessionId) {
      return existingSessionId;
    }

    // Create new session with AgentFlow
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Store session ID in localStorage
    localStorage.setItem(this.getSessionKey(userId), sessionId);
    
    return sessionId;
  }

  // Save a message to localStorage cache
  async saveMessage(
    userId: string,
    sessionId: string,
    messageType: 'user' | 'assistant',
    content: string
  ): Promise<ChatMessage> {
    if (!userId || !sessionId || !messageType || !content) {
      throw new Error('Missing required parameters for saving message');
    }

    if (content.length > 10000) {
      throw new Error('Message content too long');
    }

    const message: ChatMessage = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      role: messageType,
      content,
      timestamp: new Date().toISOString(),
    };

    // Save to localStorage cache
    const key = this.getStorageKey(userId, sessionId);
    const existingMessages = this.getMessages(userId, sessionId);
    const updatedMessages = [...existingMessages, message];
    
    // Keep only last 250 messages to avoid performance issues
    const MAX_MESSAGES = 250;
    if (updatedMessages.length > MAX_MESSAGES) {
      updatedMessages.splice(0, updatedMessages.length - MAX_MESSAGES);
    }
    
    localStorage.setItem(key, JSON.stringify(updatedMessages));
    return message;
  }

  // Get messages from localStorage cache
  getMessages(userId: string, sessionId: string): ChatMessage[] {
    const key = this.getStorageKey(userId, sessionId);
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  }

  // Get chat history with pagination (from localStorage cache)
  async getChatHistory(
    userId: string,
    sessionId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<ChatMessage[]> {
    const allMessages = this.getMessages(userId, sessionId);
    return allMessages.slice(offset, offset + limit);
  }

  // Removed AgentFlow API methods - using local storage only for MVP

  // Send message to AgentFlow and get response (simplified for MVP)
  // Send message to API with context
  async sendMessageToAPI(
    userId: string,
    sessionId: string,
    message: string,
    outletInfo?: any
  ): Promise<{ response: string; chatHistory: Array<{role: 'user' | 'assistant'; content: string; timestamp?: string}> }> {
    try {
      // Send to AgentFlow API with outlet info
      const response = await agentFlowApi.sendMessageWithOutlet(
        message, // Clean message without context prefix
        userId,
        outletInfo
      );

      if (!response || !response.text) {
        throw new Error('No response from AgentFlow');
      }

      // Save messages to localStorage (clean messages for UI)
      await this.saveMessage(userId, sessionId, 'user', message);
      await this.saveMessage(userId, sessionId, 'assistant', response.text);

      // Return response and updated chat history
      const updatedHistory = this.getMessages(userId, sessionId).map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      }));

      return {
        response: response.text,
        chatHistory: updatedHistory
      };
    } catch (error) {
      console.error('Error sending message to AgentFlow:', error);
      throw error;
    }
  }

  // Legacy method for backward compatibility
  async sendMessageToAgentFlow(
    userId: string,
    sessionId: string,
    message: string,
    outletName?: string,
    contactPerson?: string,
    contextualMessage?: string
  ): Promise<{ response: string; chatHistory: Array<{role: 'user' | 'assistant'; content: string; timestamp?: string}> }> {
    // Use the new method
    return this.sendMessageToAPI(userId, sessionId, message);
  }

  // Delete all messages for a user
  async deleteAllMessages(userId: string): Promise<void> {
    // Clear all chat data for this user
    const keys = Object.keys(localStorage).filter(key => 
      key.startsWith(`agentflow_chat_${userId}_`) || 
      key.startsWith(`agentflow_session_${userId}`)
    );
    keys.forEach(key => localStorage.removeItem(key));
  }

  // Delete all messages for a specific session
  async deleteSessionMessages(userId: string, sessionId: string): Promise<void> {
    const key = this.getStorageKey(userId, sessionId);
    localStorage.removeItem(key);
  }

  // Get message count for pagination
  async getMessageCount(userId: string, sessionId: string): Promise<number> {
    return this.getMessages(userId, sessionId).length;
  }

  // Get all sessions for a user
  async getUserSessions(userId: string, limit: number = 10): Promise<ChatSession[]> {
    const sessionId = localStorage.getItem(this.getSessionKey(userId));
    if (!sessionId) {
      return [];
    }

    return [{
      id: sessionId,
      user_id: userId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      is_active: true
    }];
  }

  // Removed sync with AgentFlow - using local storage only for MVP

  // Get chat history (local only - no API sync)
  async getChatHistoryWithSync(
    userId: string,
    sessionId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<ChatMessage[]> {
    return this.getChatHistory(userId, sessionId, limit, offset);
  }

  // Get chat history immediately (local only)
  async getChatHistoryFast(
    userId: string,
    sessionId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<ChatMessage[]> {
    return this.getChatHistory(userId, sessionId, limit, offset);
  }

  // Removed sync methods - using local storage only for MVP
}

// Export the service
export const agentFlowChatService = new AgentFlowChatService();
export default agentFlowChatService;
